/**
 * Decorative shapes, as picker slots (DS-7).
 *
 * Two builders, one engine, two entry paths:
 *
 *   · `buildDecorativeReplaceSlots` — one slot per decorative already in a
 *     section. THIS IS THE GAP THE IMAGE QUEUE LEFT: `image-slot-queue.js:24-28`
 *     excludes decoratives by construction, with the comment that they route to
 *     the decorative panel. They did, and the panel could only replace one at a
 *     time.
 *   · `buildPageEdgeSlots` — one slot per section edge, walking the page from
 *     where the user entered, so dressing five boundaries is one dialog.
 *
 * Pure, like `image-slot-queue.js` and for the same reason: one HTML string (or
 * one page object) in, an array of slot descriptors out. No store, no bridge,
 * no `document` beyond `DOMParser`. `image-editor.js` reaches the store, the
 * bridge and the shell's toast on import; this rule set is the part with edge
 * cases worth driving directly, and it can be.
 *
 * Both inherit the picker's two rules VERBATIM:
 *   · **`< 2` returns `[]`** — a queue of one is a chip that can only be what
 *     it already is, and returning `[]` is how a builder says "nothing to
 *     queue" so the dialog renders its single-target shape.
 *   · **Rotate rather than sort** — the slots stay in document order, which is
 *     the order they appear down the page, so the queue reads top-to-bottom
 *     from wherever it was entered.
 *
 * ADDRESSED BY `eid`, NEVER BY POSITIONAL INDEX. `listDecorativeSvgs` and its
 * siblings address by index into `querySelectorAll` — the exact pattern
 * `builder/CLAUDE.md` forbids across sibling units, and a slot filled after an
 * earlier one was inserted would land on the wrong shape. `removeDecorativeByEid`
 * is the identity-based precedent; these follow it.
 */

import { listDecorativeSvgs, serializeDecorativePreview } from './insert-decorative-svg.js';

/** Above this, a "ratio" is a pair of large coprime numbers telling nobody anything. */
const RATIO_MAX_TERM = 32;

/**
 * One slot per decorative already in this section.
 *
 * @param {string} sectionHtml
 * @param {{eid?: string, instanceId?: string, sectionPath?: string}} [target]
 *   the shape the user clicked, for rotation and for the `data` bag each pick
 *   is applied through.
 * @returns {Array<object>} slots, or `[]` when there are fewer than two
 */
export function buildDecorativeReplaceSlots(sectionHtml, target = {}) {
  if (!sectionHtml) return [];
  let items;
  try {
    items = listDecorativeSvgs(sectionHtml);
  } catch {
    // A section whose HTML will not parse is not a reason to refuse the whole
    // picker — it is a reason to open it on the one target the caller has.
    return [];
  }

  const slots = [];
  for (const item of items) {
    // Unaddressable ⇒ not a slot. A pick could not be applied to it, and a chip
    // that cannot be filled is worse than an absent one.
    if (!item.eid) continue;
    // <img> decoratives are replaced through the image pipeline, not this one.
    if (item.tagName !== 'svg') continue;
    slots.push({
      id: item.eid,
      label: item.name || `Shape ${slots.length + 1}`,
      ratio: ratioFromViewBoxAttr(item.markup),
      // The shape's own preview, painted with the token it actually carries —
      // the chip is a PICTURE of the thing you are about to change.
      thumb: previewDataUri(safePreview(item.markup, item.colorToken)),
      filled: false,
      data: {
        eid: item.eid,
        instanceId: target.instanceId || '',
        sectionPath: target.sectionPath || '',
        colorToken: item.colorToken || 'original'
      }
    });
  }

  if (slots.length < 2) return [];
  return rotateTo(slots, target.eid);
}

/**
 * One slot per section EDGE, walking the page.
 *
 * The edges are the gaps a user sees between sections: the top of the first
 * section, then the bottom of each. Entering at one and filling several is the
 * case this exists for — dressing five boundaries used to be five dialogs.
 *
 * @param {{sections?: Array<object>}} page
 * @param {{sectionIndex?: number, edge?: 'top'|'bottom', pageIndex?: number}} [entry]
 * @param {{displayNameOf?: (section: object) => string}} [opts]
 *   `displayNameOf` is INJECTED rather than imported, to keep this module free
 *   of `shared/section-categories.js` and therefore of the category map it
 *   reads. Absent, slots fall back to a positional label.
 * @returns {Array<object>} slots, or `[]` when there are fewer than two
 */
export function buildPageEdgeSlots(page, entry = {}, opts = {}) {
  const sections = Array.isArray(page?.sections) ? page.sections : [];
  if (!sections.length) return [];
  const nameOf =
    typeof opts.displayNameOf === 'function' ? opts.displayNameOf : (_s, i) => `Section ${i + 1}`;

  const slots = [];
  sections.forEach((section, index) => {
    // The top edge exists only for the FIRST section. Every other "top" is the
    // previous section's bottom, and offering both would be two chips for one
    // boundary — the user would fill one and see nothing change at the other.
    if (index === 0) slots.push(edgeSlot(section, index, 'top', nameOf, entry));
    slots.push(edgeSlot(section, index, 'bottom', nameOf, entry));
  });

  if (slots.length < 2) return [];
  // NO entry point ⇒ NO rotation. Defaulting the index to 0 and the edge to
  // `bottom` would silently rotate a caller that named nothing past the first
  // section's top edge — which is the one edge the top-edge work exists for.
  if (!Number.isInteger(entry.sectionIndex)) return slots;
  return rotateTo(slots, `${entry.sectionIndex}:${entry.edge || 'bottom'}`);
}

function edgeSlot(section, index, edge, nameOf, entry) {
  const name = String(nameOf(section, index) || `Section ${index + 1}`);
  return {
    id: `${index}:${edge}`,
    label: `${name} · ${edge} edge`,
    ratio: '',
    thumb: '',
    filled: false,
    data: {
      sectionIndex: index,
      edge,
      pageIndex: entry.pageIndex ?? 0,
      instanceId: section?.instanceId || ''
    }
  };
}

/**
 * Start where the user pointed, by ROTATING rather than sorting.
 *
 * Sorting would put the entry point first and everything else in an order the
 * page does not have; rotating keeps document order and simply begins
 * elsewhere, so the queue still reads top-to-bottom.
 */
function rotateTo(slots, id) {
  const at = slots.findIndex((s) => s.id === id);
  return at > 0 ? slots.slice(at).concat(slots.slice(0, at)) : slots;
}

/** A preview that never throws — a bad shape costs its own chip, not the queue. */
function safePreview(markup, colorToken) {
  try {
    return serializeDecorativePreview(markup, { colorToken: colorToken || 'original' });
  } catch {
    return '';
  }
}

function previewDataUri(markup) {
  return markup ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}` : '';
}

/**
 * `12:1` from the shape's own `viewBox`.
 *
 * Read off the STORED markup rather than a rendered box: this runs against a
 * parsed string with no layout. An omitted ratio is better than a guessed one.
 */
export function ratioFromViewBoxAttr(markup) {
  const match = /viewBox\s*=\s*["']([^"']+)["']/i.exec(String(markup || ''));
  if (!match) return '';
  const parts = match[1]
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length !== 4) return '';
  const [, , w, h] = parts;
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return '';
  const rw = Math.round(w);
  const rh = Math.round(h);
  const divisor = greatestCommonDivisor(rw, rh);
  const a = rw / divisor;
  const b = rh / divisor;
  if (a > RATIO_MAX_TERM || b > RATIO_MAX_TERM) return `${(w / h).toFixed(2)}:1`;
  return `${a}:${b}`;
}

function greatestCommonDivisor(a, b) {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}
